import {
  Component, DestroyRef, ElementRef, EventEmitter, HostListener, Injector, Input, OnChanges, Output,
  SimpleChanges, afterNextRender, inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

export interface MultiSelectItem {
  id: string;
  label: string;
  sublabel?: string;
}

export interface MultiSelectPage {
  items: MultiSelectItem[];
  total: number;
}

/**
 * Searchable multi-select modal (server-side search + "load more").
 * Selection is edited on a working copy and only emitted on Apply.
 * Visual language follows supervisor-picker-modal.
 */
@Component({
  selector: 'app-multi-select-modal',
  standalone: false,
  templateUrl: './multi-select-modal.component.html',
  styleUrl: './multi-select-modal.component.scss',
})
export class MultiSelectModalComponent implements OnChanges {
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  @Input() isOpen = false;
  @Input() title = 'Select values';
  @Input() subtitle?: string;
  @Input() searchPlaceholder = 'Search…';
  @Input() emptyText = 'No values available.';
  @Input() selected: MultiSelectItem[] = [];
  @Input({ required: true }) fetchPage!: (search: string, page: number) => Observable<MultiSelectPage>;

  @Output() apply = new EventEmitter<MultiSelectItem[]>();
  @Output() closed = new EventEmitter<void>();

  items: MultiSelectItem[] = [];
  total = 0;
  loading = false;
  loadingMore = false;
  error: string | null = null;
  search = '';
  showSelectedOnly = false;
  readonly working = new Map<string, MultiSelectItem>();

  private page = 1;
  private request?: Subscription;
  private readonly search$ = new Subject<string>();

  constructor() {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(true));
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) this.open();
  }

  get selectedCount(): number {
    return this.working.size;
  }

  get visibleItems(): MultiSelectItem[] {
    return this.showSelectedOnly ? [...this.working.values()] : this.items;
  }

  get canLoadMore(): boolean {
    return !this.showSelectedOnly && !this.loading && this.items.length < this.total;
  }

  onSearch(value: string): void {
    this.search = value;
    this.showSelectedOnly = false;
    this.search$.next(value.trim());
  }

  isSelected(id: string): boolean {
    return this.working.has(id);
  }

  toggle(item: MultiSelectItem): void {
    if (this.working.has(item.id)) this.working.delete(item.id);
    else this.working.set(item.id, item);
  }

  onItemKeydown(event: KeyboardEvent, item: MultiSelectItem): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggle(item);
    }
  }

  clear(): void {
    this.working.clear();
    this.showSelectedOnly = false;
  }

  loadMore(): void {
    this.page++;
    this.load(false);
  }

  retry(): void {
    this.load(true);
  }

  confirm(): void {
    this.apply.emit([...this.working.values()]);
    this.closed.emit();
  }

  close(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }

  private open(): void {
    this.working.clear();
    this.selected.forEach(item => this.working.set(item.id, item));
    this.search = '';
    this.showSelectedOnly = false;
    this.items = [];
    this.total = 0;
    this.load(true);
    afterNextRender(() => this.host.nativeElement.querySelector<HTMLInputElement>('.search-input-custom')?.focus(), {
      injector: this.injector,
    });
  }

  private load(reset: boolean): void {
    if (reset) this.page = 1;
    this.loading = reset;
    this.loadingMore = !reset;
    this.error = null;
    this.request?.unsubscribe();
    this.request = this.fetchPage(this.search.trim(), this.page).subscribe({
      next: res => {
        this.items = reset ? res.items : [...this.items, ...res.items];
        this.total = res.total;
        this.loading = false;
        this.loadingMore = false;
      },
      error: () => {
        this.error = 'Could not load values.';
        this.loading = false;
        this.loadingMore = false;
      },
    });
  }
}
